import { Icon } from '@components/primitives/Icon'
import { CloudflareImage } from '@components/primitives/Image/Cloudflare'
import Tab from '@components/primitives/Tab'
import { Text } from '@components/primitives/Text'
import { faClose } from '@fortawesome/pro-regular-svg-icons/faClose'
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser'
import { cityStatecodeString } from '@modules/trials/helpers/parseLocationsFromTrial/parseTruncatedLocationsFromTrial'
import { useState } from 'react'
import { type TrialLocationInMap } from './Trial2ListingMap.client'

const CloseButton = ({ onClose }: { onClose: () => void }) => {
  return (
    <div
      aria-label='Close'
      className='inline size-10 rounded-full border border-neutral300 bg-white hover:cursor-pointer'
      data-testid='modal-close'
      onClick={onClose}
      tabIndex={0}
    >
      <Icon
        aria-label='Close'
        className={'ml-[-1px] w-10 pt-[11px]  text-dark'}
        icon={faClose}
      />
    </div>
  )
}

const TABS = ['Teams'] as const
type SelectedLocationSidebarTabs = (typeof TABS)[number]

export const Trial2SelectedLocationSidebar = ({
  location,
  unselectLocation,
}: {
  location: TrialLocationInMap
  unselectLocation: () => void
}) => {
  const [selectedTab, setSelectedTab] = useState<SelectedLocationSidebarTabs>(
    TABS[0],
  )
  // TODO - there is a bug here (it is throwing an infinite render)
  // const { Distance } = useTrialAttributes([location])
  const Distance = undefined

  return (
    <div className='h-72 w-full overflow-auto bg-white p-6 md:h-[500px] md:w-1/2'>
      <div className=''>
        <div className='flex justify-between'>
          <Text styleName='h4' value={location.facility} />
          <CloseButton onClose={unselectLocation} />
        </div>
        <div className='flex justify-between'>
          <div className='my-1'>
            <Text
              className='text-neutral600'
              styleName='p'
              value={`${cityStatecodeString(location)} ${
                Distance ? ` ${Distance}` : ''
              }`}
            />
          </div>
        </div>
      </div>
      <div className='flex border-y-2 border-neutral200'>
        {TABS.map((tab) => (
          <Tab
            key={tab}
            label={tab}
            onClick={() => setSelectedTab(tab)}
            selected={selectedTab === tab}
            selectedBorderColor='border-neutral600'
            selectedFontColor='text-black'
          />
        ))}
      </div>
      <SelectedLocationTabs activeTab={selectedTab} location={location} />
    </div>
  )
}

const SelectedLocationTabs = ({
  activeTab,
  location,
}: {
  activeTab: SelectedLocationSidebarTabs
  location: TrialLocationInMap
}) => {
  const contactsToShow = location.contacts
    .sort((a, b) => {
      // Sort PIs first
      if (
        a.contact.role === 'Principal Investigator' &&
        b.contact.role !== 'Principal Investigator'
      ) {
        return -1
      }
      if (
        b.contact.role === 'Principal Investigator' &&
        a.contact.role !== 'Principal Investigator'
      ) {
        return 1
      }
      return 0
    })
    .filter(
      (contact, index, self) =>
        // Keep first occurrence of each name (which will be PI if exists due to sort)
        index ===
        self.findIndex((c) => c.contact.name === contact.contact.name),
    )
  if (activeTab === 'Teams') {
    if (!location || location.contacts.length === 0) {
      return (
        <Text
          className='mt-24 px-4 text-center'
          value={`Sorry! We don't have any contacts on file for the trial site.`}
        />
      )
    }
    return (
      <div className='mt-4 grid grid-cols-1 gap-3 pl-2'>
        {contactsToShow.map((contact) => (
          <div
            className='flex items-center gap-6 rounded-xl border border-neutral300 p-3'
            key={contact.contact.id}
          >
            {contact.contact.principalInvestigator?.profilePhoto?.externalId ? (
              <div className='size-[65px]'>
                <CloudflareImage
                  alt={'Profile picture of ' + contact.contact.name}
                  className='rounded-full bg-contain'
                  height={80}
                  imageId={
                    contact.contact.principalInvestigator?.profilePhoto
                      ?.externalId
                  }
                  width={80}
                />
              </div>
            ) : (
              <Icon
                className='rounded-full bg-neutral200 p-3 text-neutral500'
                icon={faUser}
                size='3x'
              />
            )}
            <div>
              <Text styleName='p-small-strong' value={contact.contact.name} />
              <Text
                className='text-neutral600'
                styleName='p-small'
                value={contact.contact.role}
              />
            </div>
          </div>
        ))}
      </div>
    )
  }
  return <></>
}
